import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import { CustomersKeyframes, PlusIcon, SearchIcon } from '../containers/customer/CustomersUI';
import { EmptyState, LoadingLine, SearchField, TagIcon } from '../containers/menu/MenuUI';
import MenuItemCard from '../containers/menu/MenuItemCard';
import MenuItemDialog from '../containers/menu/MenuItemDialog';
import DeleteMenuItemDialog from '../containers/menu/DeleteMenuItemDialog';
import {
  categoryOf,
  createMenuItem,
  deleteMenuItem,
  fetchMenu,
  updateMenuItem,
} from '../containers/menu/menuApi';

import {
  addProduct,
  removeProduct,
  setProductDataFetched,
  setProducts,
  updateProduct,
} from '../containers/orders/productSlice';
import {
  selectCategory,
  setCategories,
  setCategoryDataFetched,
} from '../containers/orders/categorySlice';

/**
 * The menu board.
 *
 * The phone lists menu items as one flat column you long-press to edit. A till
 * has the room to show them as they are chosen — a grid of cards with the
 * picture on top — and the two things you do to a card sit on the card.
 *
 * Items and categories come from one call, the phone's own
 * `/menu/get-business-menu-items`, and land in the same two slices the orders
 * screen reads, so adding a dish here puts it on the order pad immediately
 * without a second fetch.
 *
 * Categories themselves are managed under Administration, next to users —
 * naming a category and pointing it at a printer is setup, not a thing done
 * mid-service.
 */
export default function Menu() {
  const dispatch = useDispatch();

  const activeUser = useSelector((state) => state.login.activeUser);
  const { business } = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const { products, isProductsDataFetched } = useSelector((state) => state.products);
  const { categories, selected, isCategoryDataFetched } = useSelector(
    (state) => state.categories
  );

  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);
  const [deleting, setDeleting] = React.useState(null);

  const currency = business?.currencyMark || '$';

  // Waiters take orders; changing the menu is not their job.
  const canEdit = activeUser?.role !== 'waiter';

  /* ── the board ──────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (!business?._id) return;
    if (isProductsDataFetched && isCategoryDataFetched) return;

    let cancelled = false;
    setLoading(true);

    fetchMenu({ businessId: business._id, token })
      .then(({ menuItems, categories: rows }) => {
        if (cancelled) return;
        dispatch(setProducts(menuItems));
        dispatch(setProductDataFetched(true));
        dispatch(setCategories(rows));
        dispatch(setCategoryDataFetched(true));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(err?.response?.data?.message || 'Could not load the menu');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [business, token, isProductsDataFetched, isCategoryDataFetched, dispatch]);

  /* ── writing ────────────────────────────────────────────────────────── */

  /**
   * One handler for adding and for correcting, because the dialog is the same
   * dialog — it passes back the item it was editing, or nothing for a new one.
   */
  const submitItem = async ({ item, name, price, category, imageUrl }) => {
    if (item) {
      const saved = await updateMenuItem({
        id: item._id,
        payload: { name, price, category, imageUrl },
        token,
      });
      dispatch(updateProduct({ _id: item._id, updated: saved || { name, price, category, imageUrl } }));
      return;
    }

    const created = await createMenuItem({
      payload: { name, price, category, imageUrl, business: business?._id },
      token,
    });
    dispatch(addProduct(created));
  };

  const removeItem = async (item) => {
    await deleteMenuItem({ id: item._id, token });
    dispatch(removeProduct(item._id));
  };

  /* ── filtering ──────────────────────────────────────────────────────── */

  const visible = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return products.filter((item) => {
      if (!item || item.status === 'unavailable') return false;
      if (selected !== 'All') {
        const id =
          item.category && typeof item.category === 'object'
            ? item.category._id
            : item.category;
        if (id !== selected) return false;
      }
      if (!query) return true;
      return String(item.name || '').toLowerCase().includes(query);
    });
  }, [products, selected, search]);

  const chips = [{ _id: 'All', name: 'All' }, ...categories];

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      {/* ── what this is, what is in it, and how to add to it ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 20,
          padding: '20px 28px 16px',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
          <span style={{ fontSize: 20, fontWeight: 700, letterSpacing: -0.2 }}>Menu</span>
          <span style={{ fontSize: 12.5, color: T.inkFaint }}>
            {visible.length} {visible.length === 1 ? 'item' : 'items'}
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 280 }}>
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Search the menu"
              icon={<SearchIcon />}
            />
          </div>

          {canEdit ? (
            <button
              type="button"
              onClick={() => setCreating(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                height: 42,
                padding: '0 18px',
                background: T.brand,
                border: 'none',
                borderRadius: T.radiusPill,
                font: 'inherit',
                fontSize: 13.5,
                fontWeight: 600,
                color: T.surface,
                cursor: 'pointer',
              }}
            >
              <PlusIcon />
              <span>Ku Dar</span>
            </button>
          ) : null}
        </div>
      </div>

      {/* ── categories ── */}
      <div
        style={{
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          padding: '14px 28px',
          background: T.surface,
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        {chips.map((chip) => {
          const active = selected === chip._id;
          return (
            <button
              key={chip._id}
              type="button"
              onClick={() => dispatch(selectCategory(chip._id))}
              style={{
                height: 32,
                padding: '0 14px',
                background: active ? T.brand : T.surface,
                border: `1px solid ${active ? T.brand : T.borderStrong}`,
                borderRadius: T.radiusPill,
                font: 'inherit',
                fontSize: 12.5,
                fontWeight: 600,
                color: active ? T.surface : T.inkSoft,
                cursor: 'pointer',
              }}
            >
              {chip.name}
            </button>
          );
        })}
      </div>

      {/* ── the board itself ── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 28px 28px' }}>
        {loadError ? (
          <div style={{ padding: 60, textAlign: 'center' }}>
            <span style={{ fontSize: 14, color: T.danger }}>{loadError}</span>
          </div>
        ) : loading && !products.length ? (
          <LoadingLine />
        ) : visible.length ? (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(178px, 1fr))',
              gap: 16,
            }}
          >
            {visible.map((item) => (
              <MenuItemCard
                key={item._id}
                item={item}
                currency={currency}
                canEdit={canEdit}
                categoryName={categoryOf(item, categories)?.name || ''}
                onEdit={setEditing}
                onDelete={setDeleting}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<TagIcon />}
            title={search.trim() || selected !== 'All' ? 'Nothing here' : 'No menu items yet'}
          />
        )}
      </div>

      {creating ? (
        <MenuItemDialog
          categories={categories}
          onClose={() => setCreating(false)}
          onSubmit={submitItem}
        />
      ) : null}

      {editing ? (
        <MenuItemDialog
          item={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSubmit={submitItem}
        />
      ) : null}

      {deleting ? (
        <DeleteMenuItemDialog
          item={deleting}
          currency={currency}
          onClose={() => setDeleting(null)}
          onConfirm={removeItem}
        />
      ) : null}
    </div>
  );
}
